import { timer as observableTimer, Subscription } from "rxjs";
import { Component, OnInit, OnDestroy } from "@angular/core";
import { environment } from "../../environments/environment.dev";
import { BfxAuthService } from "../bfx-common/services/bfx-auth.service";
import {
  ConnectionStatusPromiseService,
  ConnectionStatus,
  ConnectionStatusState,
} from "shared/broker-client-services";
import {
  BenchmarkDataPrimaryLookupService,
  CubiqWorker,
  SystemConfigPromiseService,
} from "shared/cubiq-services";
import { BfxBlotterOrdersService } from "../bfx-blotter-orders/bfx-blotter-orders.service";
import { BfxAppStatusService } from "../bfx-common/services/bfx-app-status.service";
import {
  StaticDataProfileImpl,
  SystemControlImpl,
  SystemControlStatus,
} from "shared/cubiq-model-multi-asset";

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import {
  BfxFooterBarHandlerService,
  BfxFooterBarMessageEvent,
} from "./services/bfx-footer-bar-handler.service";
import { BfxReconnectDialogService } from "../bfx-common/services/bfx-reconnect-dialog.service";
import { BfxVwapInfo, VWAPChangeNotification } from "app/bfx-vwap/bfx-vwap-info";

@Component({
  selector: "bfx-footer-bar",
  templateUrl: "./html/bfx-footer-bar.component.html",
  standalone: false,
})
export class BfxFooterBarComponent implements OnInit, OnDestroy {
  private authSub: Subscription;
  private timerSubscription: Subscription;
  private messageHandlerSubscription: Subscription;
  private autoHedgeSub: Subscription;

  public appVersion: string;
  public now: number = Date.now();
  public userType: string = "";
  public systemName: string = "";
  public notifyMessage: string = null;

  public connectionStatus: number = 0;
  public connectionStatusText: string = "Initializing";

  public panicStatusText: string = "";
  public togglePanicStatus: SystemControlStatus;
  public panicControlTypes: any;
  public panicMessage = [
    "Order Monitoring Disabled",
    "Order Book Changes Disabled",
    "Auto Fill Disabled",
  ];
  autoHedgingDisabled: boolean = false;

  private vwapUpdateSubsciption: Subscription;
  public vwapMessage: string = null;

  constructor(
    private _authService: BfxAuthService,
    private _connectionStatusService: ConnectionStatusPromiseService,
    private _systemConfigService: SystemConfigPromiseService,
    private _blotterOrdersService: BfxBlotterOrdersService,
    private _appStatusService: BfxAppStatusService,
    private _footerBarHandlerService: BfxFooterBarHandlerService,
    private _reconnectDialogService: BfxReconnectDialogService,
    protected benchmarkDataService: BenchmarkDataPrimaryLookupService,
    protected cubiqWorker: CubiqWorker,
    protected vwapInfo: BfxVwapInfo,
  ) {
    this._connectionStatusService.getConnectionEvents().subscribe((event: ConnectionStatus) => {
      this.processStatusEvent(event);
    });

    this.messageHandlerSubscription = this._footerBarHandlerService
      .getMessageCtrlEventSubject()
      .subscribe(($event: BfxFooterBarMessageEvent) => {
        this.processNotificationMessageEvent($event);
      });

    this.vwapUpdateSubsciption = vwapInfo.vwapUpdates.subscribe(
      (update: VWAPChangeNotification) => {
        this.onVwapChangeNotification(update);
      },
    );

    this._systemConfigService.getUserType().then((value) => {
      this.userType = value;
    });
    this._systemConfigService.getSystemName().then((value) => {
      this.systemName = value;
    });
  }

  /**********************************************************************/
  /* INITIALIZATION */

  ngOnInit() {
    this.initiateTimer();
    this.appVersion = environment.version;
    this.authSub = this._authService.authStatus$.subscribe((status) => {
      if (status) {
      } else {
      }
    });
    this.processPanicControlStatus();
    this.subscribeAutoHedgeCircuitBreaker();
  }

  ngOnDestroy() {
    if (this.authSub) {
      this.authSub.unsubscribe();
    }
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }
    this.unsubscribeAutoHedgeCircuitBreaker();
  }
  /**********************************************************************/
  /* INITIALIZATION */
  public initiateTimer(): void {
    let clock = observableTimer(0, 1000);

    this.timerSubscription = clock.subscribe(
      (t) => {
        this.now = Date.now();
      },
      (err) => {
        logger.out.info("ERROR: Timer error =", err);
        this.timerSubscription.unsubscribe();
      },
      () => {
        logger.out.info("Timer completed");
        this.timerSubscription.unsubscribe();
      },
    );
  }

  public promptReconnect(): void {
    if (this._reconnectDialogService.hasReconnectDialogNoResponse()) {
      this._reconnectDialogService.promptReconnect();
    }
  }

  public get ratesPaused(): boolean {
    return this._blotterOrdersService.areRatesPaused();
  }

  public get rateResumeCounter(): number {
    return this._blotterOrdersService.getRateResumeCounter();
  }
  /**********************************************************************/
  /* EVENT HANDLERS */
  private processNotificationMessageEvent($event: BfxFooterBarMessageEvent): void {
    if ($event instanceof BfxFooterBarMessageEvent) {
      this.notifyMessage = $event.message;
      setTimeout(() => {
        this.notifyMessage = null;
      }, $event.duration);
    } else {
      // log error
    }
  }

  onVwapChangeNotification(update: VWAPChangeNotification) {
    this.vwapMessage = update.vwapString;
  }

  private processStatusEvent(event: any): void {
    this.connectionStatus = event.connectionStatus;
    switch (this.connectionStatus) {
      case ConnectionStatusState.NONE:
        this.connectionStatusText = "Initializing"; // none
        break;
      case ConnectionStatusState.CONNECTED:
        this.connectionStatusText = "Online";
        break;
      case ConnectionStatusState.ERROR:
        this.connectionStatusText = "Error";
        break;
      case ConnectionStatusState.DISCONNECTED:
      case ConnectionStatusState.RECONNECTING:
        this.connectionStatusText = "Reconnecting";
        break;
      case ConnectionStatusState.RELOAD:
        this.connectionStatusText = "Reconnected";
        break;
    }
  }

  private processPanicControlStatusMessage(panicControlEvent: SystemControlImpl[]): void {
    if (
      Array.isArray(panicControlEvent) &&
      panicControlEvent.length !== 0 &&
      this.togglePanicStatus === SystemControlStatus.OFFLINE
    ) {
      let panicMessageArr = [];

      this.panicControlTypes = panicControlEvent
        .map((panicControl) => {
          return panicControl.systemControlSubType;
        })
        .sort((a, b) => a - b);

      for (let i = 0; i < this.panicControlTypes.length; i++) {
        panicMessageArr.push(this.panicMessage[this.panicControlTypes[i]]);
      }
      this.panicStatusText = panicMessageArr.join(" / ");
    }
  }

  private processPanicControlStatus() {
    this._appStatusService.castChange.subscribe(
      (togglePanicStatusChangesEvent: SystemControlImpl[]) => {
        if (
          Array.isArray(togglePanicStatusChangesEvent) &&
          togglePanicStatusChangesEvent.length !== 0
        ) {
          if (
            togglePanicStatusChangesEvent.some(
              (item) => item.systemControlStatus === SystemControlStatus.OFFLINE,
            )
          ) {
            this.togglePanicStatus = SystemControlStatus.OFFLINE;
            this.processPanicControlStatusMessage(togglePanicStatusChangesEvent);
          }
        } else {
          this.togglePanicStatus = SystemControlStatus.ONLINE;
        }
      },
    );
  }

  subscribeAutoHedgeCircuitBreaker() {
    // prime it
    this.cubiqWorker
      .getBroker()
      .primeStaticData<StaticDataProfileImpl>(StaticDataProfileImpl.name)
      .subscribe((profile: StaticDataProfileImpl) => {
        logger.out.info(`HedgingStatusChange Prime result`);
        this.autoHedgingDisabled = !profile.autoHedgingStatus.autoHedgingEnabled;
      });

    // subscribe
    this.autoHedgeSub = this.benchmarkDataService.autoHedgeSubject.subscribe((response) => {
      logger.out.info(`HedgingStatusChange Update ${response.status.autoHedgingEnabled}`);
      this.autoHedgingDisabled = !response.status.autoHedgingEnabled;
    });
  }

  unsubscribeAutoHedgeCircuitBreaker() {
    if (this.autoHedgeSub) {
      this.autoHedgeSub.unsubscribe();
    }
  }
}
